/**
 * Data preparation. Turns the raw equations into the sampled series the charts
 * draw, and into exact point readouts for the cursor and the selection.
 *
 * The independent variable everywhere is **raw mutagen fed since evolution 0**.
 * Every chart shares it, which is what lets the four charts share one cursor,
 * one selection and one zoom domain.
 *
 * Which equations, though, is a parameter: `SeriesParams.ruleset` carries the
 * branch, and every function here reads it rather than reaching for the live
 * numbers. Anything cached — milestone crossings, the explorer ceiling — is
 * cached per branch, because the same evolution costs a different amount of
 * mutagen on each.
 *
 * Sampling note: the curves are drawn from sampled points, but every number the
 * UI *reports* (cursor readout, selection endpoints, deltas) comes from
 * `evaluateAt`, which runs the real feeding calculation at that exact mutagen
 * value. Nothing user-visible is interpolated.
 */

import { DEFAULTS } from './constants';
import { toDisplayMutagen } from './feeding';
import {
  DEFAULT_RULESET_ID,
  RULESETS,
  feedMutagen,
  marginalRatioMinutes,
  mutagenToReachEvo,
  threatIncomePerMinute,
  type Ruleset,
} from './rulesets';
import {
  MILESTONES,
  bossBiterModifiers,
  bossSpawnAttempts,
  maxGroupSize,
  normalBiterModifiers,
  unitComposition,
  type Milestone,
  type StrengthModifiers,
  type TierWeights,
} from './strength';

export interface SeriesParams {
  /** Which branch's equations to run. */
  ruleset: Ruleset;
  /** Connected players on both teams — only affects instant threat. */
  playerCount: number;
  /** `storage.max_reanim_thresh`. */
  maxReanimThresh: number;
}

export const DEFAULT_SERIES_PARAMS: SeriesParams = {
  ruleset: RULESETS[DEFAULT_RULESET_ID],
  playerCount: DEFAULTS.playerCount,
  maxReanimThresh: DEFAULTS.maxReanimThresh,
};

export interface Sample {
  /** Raw mutagen fed since evolution 0. */
  mutagen: number;
  /** `mutagen * 10000` — the "Mutagen strength" number shown in game. */
  displayMutagen: number;
  /** Uncapped evolution, 1 = 100%. */
  evo: number;
  /** Cumulative instant threat generated by feeding this much mutagen. */
  instantThreat: number;
  /** Passive threat income at this point, per minute. */
  threatPerMinute: number;
  /**
   * Minutes of passive income the *next* unit of mutagen's instant threat is
   * worth — `marginalRatioMinutes`. Zero on a branch with no instant threat.
   */
  ratioMinutes: number;
  normal: StrengthModifiers;
  boss: StrengthModifiers;
  composition: TierWeights;
  groupSize: number;
  bossAttempts: number;
}

/** Exact state after feeding `mutagen` from evolution 0. */
export function evaluateAt(mutagen: number, params: SeriesParams): Sample {
  const m = Math.max(mutagen, 0);
  const effects = feedMutagen(params.ruleset, 0, m, params.playerCount, params.maxReanimThresh);
  const evo = effects.evoIncrease;
  return {
    mutagen: m,
    displayMutagen: toDisplayMutagen(m),
    evo,
    instantThreat: effects.threatIncrease,
    threatPerMinute: threatIncomePerMinute(effects),
    ratioMinutes: marginalRatioMinutes(params.ruleset, evo, params.playerCount),
    normal: normalBiterModifiers(evo, params.maxReanimThresh),
    boss: bossBiterModifiers(evo, params.maxReanimThresh),
    composition: unitComposition(evo),
    groupSize: maxGroupSize(evo),
    bossAttempts: bossSpawnAttempts(evo),
  };
}

export type Domain = readonly [number, number];

/** Evolution the explorer refuses to scroll past. */
export const MAX_EXPLORER_EVO = 12;

const maxMutagenCache = new Map<string, number>();

/**
 * The transcribed branches contribute one key each and never another, but the
 * tuning scenario contributes a fresh one per parameter set — so a slider drag
 * would grow these maps without bound. They exist to spare a repeat of the
 * *current* view, not to remember every view ever held, so the whole map is
 * dropped once it outgrows a handful of branches.
 */
const CACHE_LIMIT = 24;

function remember<T>(cache: Map<string, T>, key: string, value: T): T {
  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(key, value);
  return value;
}

/**
 * Practical ceiling for the explorer — the mutagen that reaches 1200%
 * evolution. Per branch, and by a wide margin: live's rate freezes past 100%,
 * putting its ceiling at 18.5k mutagen, while the test branch never stops
 * charging more and lands around 1.1M.
 */
export function maxMutagenFor(ruleset: Ruleset): number {
  const cached = maxMutagenCache.get(ruleset.cacheKey);
  if (cached !== undefined) return cached;
  return remember(maxMutagenCache, ruleset.cacheKey, mutagenToReachEvo(ruleset, MAX_EXPLORER_EVO, 0));
}

/**
 * Build the drawing samples for a visible mutagen domain.
 *
 * Uniform spacing alone is a poor fit: evolution climbs almost vertically in
 * the first fraction of a percent of the default domain. So the sample set is
 * the union of a uniform grid, a log-spaced grid (which resolves that spike at
 * any zoom level) and the milestone crossings.
 */
export function buildSeries(
  domain: Domain,
  params: SeriesParams,
  uniformCount = 420,
  logCount = 200,
): Sample[] {
  const [lo, hi] = domain;
  if (!(hi > lo)) return [];

  const points = new Set<number>();
  for (let i = 0; i <= uniformCount; i += 1) {
    points.add(lo + ((hi - lo) * i) / uniformCount);
  }

  const logLo = Math.max(lo, hi * 1e-7);
  if (logLo > 0 && hi > logLo) {
    const logStart = Math.log(logLo);
    const logEnd = Math.log(hi);
    for (let i = 0; i <= logCount; i += 1) {
      points.add(Math.exp(logStart + ((logEnd - logStart) * i) / logCount));
    }
  }

  for (const crossing of milestoneCrossings(params)) {
    if (crossing.mutagen > lo && crossing.mutagen < hi) {
      // Two points so the guide line lands exactly on the curve.
      points.add(crossing.mutagen);
      points.add(crossing.mutagen * (1 + 1e-9));
    }
  }

  return [...points].sort((a, b) => a - b).map((m) => evaluateAt(m, params));
}

export interface MilestoneCrossing extends Milestone {
  /** Mutagen at which this evolution level is reached, starting from 0. */
  mutagen: number;
  displayMutagen: number;
}

const crossingCache = new Map<string, MilestoneCrossing[]>();

/**
 * Mutagen cost of each milestone. Independent of difficulty and player count
 * (difficulty scales flasks into mutagen, not mutagen into evolution) but very
 * much dependent on the branch, so the cache is keyed by it.
 */
export function milestoneCrossings(params: SeriesParams): MilestoneCrossing[] {
  const { ruleset } = params;
  const cached = crossingCache.get(ruleset.cacheKey);
  if (cached) return cached;
  const crossings = MILESTONES.map((milestone) => {
    const mutagen = mutagenToReachEvo(ruleset, milestone.evo, 0);
    return { ...milestone, mutagen, displayMutagen: toDisplayMutagen(mutagen) };
  });
  return remember(crossingCache, ruleset.cacheKey, crossings);
}

/** Mutagen domain that ends exactly at `maxEvo`. */
export function domainForMaxEvo(maxEvo: number, ruleset: Ruleset): Domain {
  return [0, mutagenToReachEvo(ruleset, maxEvo, 0)];
}

export const ZOOM_PRESETS: { label: string; maxEvo: number }[] = [
  { label: '100%', maxEvo: 1 },
  { label: '200%', maxEvo: 2 },
  { label: '330%', maxEvo: 3.3 },
  { label: '500%', maxEvo: 5 },
];

export const DEFAULT_MAX_EVO = 1.5;

/**
 * Evolution ticks for the secondary "evolution" axis. Only the ones that fall
 * inside the visible mutagen domain are returned.
 */
export function evoAxisTicks(
  domain: Domain,
  params: SeriesParams,
): { evo: number; mutagen: number }[] {
  const [lo, hi] = domain;
  const evoLo = evaluateAt(lo, params).evo;
  const evoHi = evaluateAt(hi, params).evo;
  const span = evoHi - evoLo;
  const candidateSteps = [0.005, 0.01, 0.02, 0.05, 0.1, 0.25, 0.5, 1, 2, 5];
  const step = candidateSteps.find((s) => span / s <= 9) ?? 10;

  const ticks: { evo: number; mutagen: number }[] = [];
  const first = Math.ceil(evoLo / step) * step;
  for (let evo = first; evo <= evoHi + 1e-9; evo += step) {
    const rounded = Math.round(evo / step) * step;
    // Evolution 0 sits on the y axis; the origin already says so.
    if (rounded <= 0) continue;
    const mutagen = mutagenToReachEvo(params.ruleset, rounded, 0);
    if (mutagen >= lo && mutagen <= hi) ticks.push({ evo: rounded, mutagen });
  }
  return ticks;
}

/**
 * Invert the cumulative instant-threat curve: the mutagen level at which the
 * threat generated since `startMutagen` reaches `targetThreat`.
 *
 * Cumulative threat is strictly increasing in mutagen, so a bisection on the
 * real curve is exact. Past 100% evolution it grows quadratically, so the
 * bracketing expansion terminates in a handful of doublings. Returns
 * the branch ceiling when the target is out of reach inside the explorer's
 * range — and returns `startMutagen` unmoved on a branch that has no instant
 * threat to solve for.
 */
export function mutagenForThreat(
  startMutagen: number,
  targetThreat: number,
  params: SeriesParams,
): number {
  const start = Math.max(startMutagen, 0);
  if (!(targetThreat > 0) || !params.ruleset.hasInstantThreat) return start;

  const ceiling = maxMutagenFor(params.ruleset);
  const goal = evaluateAt(start, params).instantThreat + targetThreat;

  let lo = start;
  let hi = Math.min(Math.max(start * 2, 1e-4), ceiling);
  for (let i = 0; i < 80 && hi < ceiling; i += 1) {
    if (evaluateAt(hi, params).instantThreat >= goal) break;
    hi = Math.min(hi * 2, ceiling);
  }
  if (evaluateAt(hi, params).instantThreat < goal) return ceiling;

  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    if (evaluateAt(mid, params).instantThreat < goal) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

export interface RangeSummary {
  from: Sample;
  to: Sample;
  /** Mutagen contained in the selection. */
  mutagen: number;
  /** Instant threat added across the range — the difference of the cumulative curve. */
  instantThreat: number;
  /**
   * The same range fed as one send starting from the range's own evolution.
   * On the live branch it differs from `instantThreat` by a couple of percent,
   * because the game's integrator uses coarser steps for one big send; showing
   * both makes the path-independence of feeding visible.
   */
  instantThreatSingleSend: number;
  evoGain: number;
  threatPerMinuteGain: number;
}

export function summariseRange(from: number, to: number, params: SeriesParams): RangeSummary {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const a = evaluateAt(lo, params);
  const b = evaluateAt(hi, params);
  const singleSend = feedMutagen(
    params.ruleset,
    a.evo,
    hi - lo,
    params.playerCount,
    params.maxReanimThresh,
  );
  return {
    from: a,
    to: b,
    mutagen: hi - lo,
    instantThreat: b.instantThreat - a.instantThreat,
    instantThreatSingleSend: singleSend.threatIncrease,
    evoGain: b.evo - a.evo,
    threatPerMinuteGain: b.threatPerMinute - a.threatPerMinute,
  };
}
